import { z } from 'zod';
/**
 * Munkaido es automatikus statuszok.
 *
 * A logika SZANDEKOSAN a kozos csomagban van: ugyanazt a savot hasznalja a
 * szerver (kell-e ertesitest kuldeni) es a kliens (mutassuk-e a "munkaidon
 * kivul" statuszt). Ha ket helyen lenne megirva, elobb-utobb elcsusznanak,
 * es a felhasznalo azt latna, hogy elerheto, mikozben nem kap ertesitest.
 */
// ─── Munkaido ─────────────────────────────────────────────────────────────────
export const workDayModes = ['every', 'weekdays', 'custom'];
export const workDayModeSchema = z.enum(workDayModes);
export const WORK_DAY_LABELS = {
    every: 'Minden nap',
    weekdays: 'Hétköznap',
    custom: 'Egyedi',
};
export const workingHoursSchema = z.object({
    days: workDayModeSchema,
    /** Percben ejfeltol. Ha a ketto egyenlo, nincs korlat. */
    fromMinute: z.number().int().min(0).max(1439),
    toMinute: z.number().int().min(0).max(1439),
    /** Egyedi napok, ahol 0 = vasarnap. Csak `custom` eseten szamit. */
    customDays: z.array(z.number().int().min(0).max(6)),
});
export const DEFAULT_WORKING_HOURS = {
    days: 'weekdays',
    fromMinute: 0,
    toMinute: 0,
    customDays: [],
};
const WEEKDAYS = [1, 2, 3, 4, 5];
/** Az adott nap (0 = vasarnap) munkanap-e a beallitas szerint? */
export function isWorkDay(hours, day) {
    if (hours.days === 'every')
        return true;
    if (hours.days === 'weekdays')
        return WEEKDAYS.includes(day);
    // Ures egyedi lista = nincs munkanap. Ez ervenyes valasztas (pl. szabadsag).
    return hours.customDays.includes(day);
}
/**
 * Munkaidoben van-e a felhasznalo?
 *
 * A savot a SAJAT idozonajaban ertelmezzuk: aki mas orszagbol dolgozik, ne
 * kapjon ertesitest hajnalban azert, mert a szerver Budapesten all.
 */
export function isWithinWorkingHours(hours, at, timezone) {
    const local = localParts(at, timezone);
    if (!isWorkDay(hours, local.day))
        return false;
    // Egyenlo hatarok = nincs idobeli korlat, csak a napok szamitanak.
    if (hours.fromMinute === hours.toMinute)
        return true;
    const minute = local.hour * 60 + local.minute;
    // Ejfelen atnyulo sav (pl. 22:00-06:00): ilyenkor a ket resz uniója kell.
    if (hours.fromMinute > hours.toMinute) {
        return minute >= hours.fromMinute || minute < hours.toMinute;
    }
    return minute >= hours.fromMinute && minute < hours.toMinute;
}
/** Ora, perc es a het napja a felhasznalo idozonajaban. */
function localParts(at, timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
    const parts = Object.fromEntries(formatter.formatToParts(at).map((part) => [part.type, part.value]));
    const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
        day: days[parts.weekday ?? 'Mon'] ?? 1,
        // A 24-es ora ejfelt jelent egyes kornyezetekben.
        hour: Number(parts.hour ?? '0') % 24,
        minute: Number(parts.minute ?? '0'),
    };
}
/** "9:00 - 17:00" alaku, emberi olvasatra. */
export const formatMinute = (minute) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
export function describeWorkingHours(hours) {
    const days = WORK_DAY_LABELS[hours.days];
    if (hours.fromMinute === hours.toMinute)
        return `${days}, egész nap`;
    return `${days}, ${formatMinute(hours.fromMinute)}–${formatMinute(hours.toMinute)}`;
}
// ─── Automatikus statuszok ────────────────────────────────────────────────────
export const autoStatusKinds = ['huddle', 'meeting', 'focus', 'after_hours'];
export const autoStatusKindSchema = z.enum(autoStatusKinds);
export const AUTO_STATUS = {
    huddle: { emoji: '🎧', text: 'Ölelésben' },
    meeting: { emoji: '🗓️', text: 'Megbeszélésen' },
    focus: { emoji: '🔭', text: 'Fókusz módban' },
    after_hours: { emoji: '🌙', text: 'Munkaidőn kívül' },
};
export const autoStatusFlagsSchema = z.object({
    huddle: z.boolean(),
    /**
     * "Megbeszelesen" az Outlook-naptar alapjan (docs/23, B1). Alapbol be; a regi
     * kliensek nem kuldik - ezert van alaperteke.
     */
    meeting: z.boolean().default(true),
    focus: z.boolean(),
    afterHours: z.boolean(),
});
/**
 * A megjelenitendo statusz.
 *
 * A sorrend a lenyeg: **a kezzel beallitott statusz mindig nyer.** Ha valaki
 * kiirta, hogy "szabadsagon", azt nem irhatja felul egy "munkaidon kivul" -
 * a sajat dontes tobbet er, mint a kikovetkeztetett allapot.
 *
 * Az automatikusok kozott a sorrend a konkretsag szerint megy: a huddle a
 * legkonkretabb (most eppen beszel), a munkaidon kivuliseg a leghalványabb.
 */
export function resolveStatus(input) {
    const now = input.now ?? new Date();
    const manualLive = input.manualExpiresAt === null || new Date(input.manualExpiresAt).getTime() > now.getTime();
    if ((input.manualEmoji || input.manualText) && manualLive) {
        return { emoji: input.manualEmoji, text: input.manualText, auto: null };
    }
    if (input.flags.huddle && input.inHuddle) {
        return { ...AUTO_STATUS.huddle, auto: 'huddle' };
    }
    if (input.flags.meeting !== false &&
        input.meeting &&
        new Date(input.meeting.from).getTime() <= now.getTime() &&
        new Date(input.meeting.until).getTime() > now.getTime()) {
        return { ...AUTO_STATUS.meeting, auto: 'meeting' };
    }
    if (input.flags.focus &&
        input.focusUntil &&
        new Date(input.focusUntil).getTime() > now.getTime()) {
        return { ...AUTO_STATUS.focus, auto: 'focus' };
    }
    if (input.flags.afterHours && !isWithinWorkingHours(input.workingHours, now, input.timezone)) {
        return { ...AUTO_STATUS.after_hours, auto: 'after_hours' };
    }
    return { emoji: null, text: null, auto: null };
}
/**
 * Gyorsvalaszto.
 *
 * A lejarati ido nem dekoracio: a legtobb "ebedelek" statusz azert marad fent
 * masnapig, mert senki nem veszi le. Az alapertelmezett lejarat ezt oldja meg.
 */
export const STATUS_PRESETS = [
    { emoji: '🍽️', text: 'Ebédel', minutes: 45 },
    { emoji: '🗓️', text: 'Meetingen', minutes: 60 },
    { emoji: '🚗', text: 'Úton', minutes: 60 },
    { emoji: '🩺', text: 'Rendelésen', minutes: 240 },
    { emoji: '🎯', text: 'Fókuszált munka', minutes: 120 },
    { emoji: '🤒', text: 'Beteg vagyok', minutes: 60 * 24 },
    { emoji: '🏠', text: 'Otthonról dolgozom', minutes: 60 * 9 },
    { emoji: '🌴', text: 'Szabadságon', minutes: null },
];
//# sourceMappingURL=presence-status.js.map